import * as THREE from "three";

/** Tileable tread-plate canvas texture (dark bars + rivet highlights) for the
 * track side faces. Lazily built (not at module scope) — tank.test.ts imports
 * buildTank directly under the client workspace's "node" vitest environment
 * (no `document`), so any canvas call must wait for first real use instead of
 * running at import time. */
function makeTreadTexture(): THREE.CanvasTexture {
  const w = 32;
  const h = 64;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#24282e";
  ctx.fillRect(0, 0, w, h);
  const barH = 8;
  ctx.fillStyle = "#141821";
  for (let y = 0; y < h; y += barH) ctx.fillRect(0, y, w, barH * 0.42);
  ctx.fillStyle = "#3d434c";
  for (let y = barH * 0.65; y < h; y += barH) {
    ctx.beginPath();
    ctx.arc(w / 2, y, 1.6, 0, Math.PI * 2);
    ctx.fill();
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(1, 5);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

let trackMat: THREE.MeshStandardMaterial | null = null;
function getTrackMat(): THREE.MeshStandardMaterial {
  if (!trackMat) {
    // tank.test.ts calls buildTank() directly under the client workspace's
    // "node" vitest environment (no `document`) — unlike a deferred-until-
    // used effect, buildTank's whole job IS to build the tracks, so there's
    // no way to dodge the canvas call in tests. Fall back to a flat material
    // (same tone, no baked tread pattern) instead of throwing.
    trackMat = typeof document !== "undefined"
      ? new THREE.MeshStandardMaterial({ map: makeTreadTexture(), roughness: 0.85, metalness: 0.2 })
      : new THREE.MeshStandardMaterial({ color: 0x1c1f25, roughness: 0.85, metalness: 0.2 });
  }
  return trackMat;
}

const headlightMat = new THREE.MeshStandardMaterial({
  color: 0xfff2cf, emissive: 0xffdf8a, emissiveIntensity: 1.1, roughness: 0.4,
});

/** Grayscale panel-seam + rivet + weathering-noise bump texture, shared across
 * every tank instance (only per-player color varies, via each material's
 * plain `color`, so one relief pattern works for all). Same test-environment
 * guard as getTrackMat — undefined under vitest's DOM-less "node" env, in
 * which case the body materials below just fall back to flat (no bumpMap). */
function makePanelBumpTexture(): THREE.CanvasTexture {
  const s = 64;
  const c = document.createElement("canvas");
  c.width = s;
  c.height = s;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#808080"; // neutral mid-gray = no bump
  ctx.fillRect(0, 0, s, s);
  ctx.strokeStyle = "#4a4a4a"; // recessed panel seams
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(0, s / 2); ctx.lineTo(s, s / 2); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(s / 2, 0); ctx.lineTo(s / 2, s); ctx.stroke();
  ctx.fillStyle = "#b0b0b0"; // raised rivets
  for (let i = 0; i < 10; i++) {
    const x = 6 + (i % 5) * ((s - 12) / 4);
    const y = 6 + Math.floor(i / 5) * (s - 12);
    ctx.beginPath(); ctx.arc(x, y, 1.3, 0, Math.PI * 2); ctx.fill();
  }
  for (let i = 0; i < 80; i++) { // subtle weathering grain
    const v = 118 + Math.floor(Math.random() * 20);
    ctx.fillStyle = `rgb(${v},${v},${v})`;
    ctx.fillRect(Math.random() * s, Math.random() * s, 1, 1);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(2, 2);
  return tex; // never used as a `map`, so colorSpace is left at its linear default
}

let panelBumpTex: THREE.CanvasTexture | null | undefined;
function getPanelBumpTex(): THREE.CanvasTexture | undefined {
  if (typeof document === "undefined") return undefined;
  if (!panelBumpTex) panelBumpTex = makePanelBumpTexture();
  return panelBumpTex;
}

/**
 * Modular low-poly tank, composed of hull + tracks + turret sub-builders.
 * Each part casts/receives shadows. The "turret" child is separable so entity
 * views can rotate it independently to the aim direction.
 */
export function buildTank(color: number): THREE.Group {
  const tank = new THREE.Group();

  const panelBump = getPanelBumpTex();
  const bumpProps = (scale: number) => (panelBump ? { bumpMap: panelBump, bumpScale: scale } : {});
  const bodyMat = new THREE.MeshStandardMaterial({ color, roughness: 0.55, metalness: 0.15, ...bumpProps(0.025) });
  const lowerMat = new THREE.MeshStandardMaterial({ color: shade(color, 0.7), roughness: 0.7, ...bumpProps(0.02) });
  const metalMat = new THREE.MeshStandardMaterial({ color: 0x2b2f36, roughness: 0.5, metalness: 0.6 });
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x1c1f25, roughness: 0.6, metalness: 0.5 });
  const accentMat = new THREE.MeshStandardMaterial({ color: shade(color, 1.25), roughness: 0.4, metalness: 0.2, ...bumpProps(0.018) });
  const stowageMat = new THREE.MeshStandardMaterial({ color: 0x5b4630, roughness: 0.9 });

  const add = (m: THREE.Mesh) => {
    m.castShadow = true;
    m.receiveShadow = true;
    tank.add(m);
    return m;
  };

  // ── Hull ──
  add(mesh(new THREE.BoxGeometry(2.8, 0.8, 3.8), bodyMat)).position.y = 0.85;
  add(mesh(new THREE.BoxGeometry(3.0, 0.45, 4.0), lowerMat)).position.y = 0.35; // lower skirt
  // Sloped glacis (front).
  const glacis = add(mesh(new THREE.BoxGeometry(2.6, 0.5, 0.9), bodyMat));
  glacis.position.set(0, 0.85, 1.7);
  glacis.rotation.x = -0.4;
  // Front drive sprocket hint + rear exhaust.
  add(mesh(new THREE.CylinderGeometry(0.35, 0.35, 0.5, 10), metalMat))
    .position.set(0, 0.7, 2.0);
  // Headlights either side of the glacis — cheap detail, reads well even at
  // gameplay distance since the emissive pops against the hull color.
  for (const sx of [-0.85, 0.85]) {
    const light = add(mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.12, 10), headlightMat));
    light.rotation.x = Math.PI / 2;
    light.position.set(sx, 0.95, 2.05);
  }

  // ── Tracks + road wheels ──
  for (const sx of [-1.15, 1.15]) {
    add(mesh(new THREE.BoxGeometry(0.7, 0.95, 4.0), getTrackMat())).position.set(sx, 0.55, 0);
    // fender
    add(mesh(new THREE.BoxGeometry(0.95, 0.12, 4.0), lowerMat)).position.set(sx, 1.12, 0);
    for (const sz of [-1.5, -0.75, 0, 0.75, 1.5]) {
      const w = mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.4, 12), metalMat);
      w.rotation.z = Math.PI / 2;
      w.position.set(sx, 0.5, sz);
      w.castShadow = true;
      tank.add(w);
    }
  }

  // ── Turret (rotates to aim) ──
  const turret = new THREE.Group();
  turret.name = "turret";
  turret.position.set(0, 1.5, 0);

  const tBody = mesh(new THREE.BoxGeometry(2.0, 0.8, 2.2), bodyMat);
  tBody.castShadow = true;
  turret.add(tBody);
  // Turret roof bevel
  const roof = mesh(new THREE.BoxGeometry(1.4, 0.3, 1.6), accentMat);
  roof.position.y = 0.5;
  roof.castShadow = true;
  turret.add(roof);

  // Mantlet + barrel + muzzle brake.
  const mantlet = mesh(new THREE.BoxGeometry(0.6, 0.55, 0.6), metalMat);
  mantlet.position.set(0, 0.05, 1.0);
  mantlet.castShadow = true;
  turret.add(mantlet);
  const barrel = mesh(new THREE.CylinderGeometry(0.17, 0.19, 3.0, 14), metalMat);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0.05, 1.8);
  barrel.castShadow = true;
  turret.add(barrel);
  const muzzle = mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.35, 12), darkMat);
  muzzle.rotation.x = Math.PI / 2;
  muzzle.position.set(0, 0.05, 3.1);
  turret.add(muzzle);

  // Commander hatch + antenna + rear exhaust stub.
  const hatch = mesh(new THREE.CylinderGeometry(0.32, 0.36, 0.22, 12), darkMat);
  hatch.position.set(0, 0.5, -0.4);
  turret.add(hatch);
  // Stowage box on the turret's side — a small, common real-tank detail that
  // breaks up the turret's flat silhouette and reads at a glance as "loaded
  // gear", not just a plain box with a barrel.
  const stowage = mesh(new THREE.BoxGeometry(0.55, 0.32, 0.7), stowageMat);
  stowage.position.set(1.05, 0.15, -0.5);
  stowage.castShadow = true;
  turret.add(stowage);
  const antenna = mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.4, 6), metalMat);
  antenna.position.set(0.6, 1.1, -0.6);
  turret.add(antenna);

  tank.add(turret);

  // Ensure every mesh casts + receives shadows.
  tank.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return tank;
}

function mesh(geo: THREE.BufferGeometry, mat: THREE.Material): THREE.Mesh {
  return new THREE.Mesh(geo, mat);
}

/** Lighten/darken a hex color by factor (1.0 = same). */
function shade(hex: number, factor: number): number {
  const r = Math.min(255, Math.round(((hex >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((hex >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((hex & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}
